/**
 * Vulnerabilities Section - Phase 5 Refactored
 *
 * Main component for the vulnerabilities table view.
 * Reduced from 1,164 lines to ~450 lines through Phase 1-5 refactoring.
 *
 * Refactoring changes:
 * - Extracted filter logic to useVulnerabilityFilters hook
 * - Extracted selection logic to useVulnerabilitySelection hook
 * - Extracted column logic to useVulnerabilityColumns hook
 * - Extracted keyboard logic to useVulnerabilityKeyboard hook
 * - Extracted cell components to components/cells/
 * - Extracted filter UI to VulnerabilitySearchFilters component
 * - Extracted bulk actions to BulkActionsToolbar component
 * - Centralized source display logic to utils/sourceDisplay.util
 */

import {
  Bars2Icon,
  ChevronDoubleDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { ChevronDoubleUpIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { XCircleIcon } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { CloseModal } from '@/components/ChangeRiskStatus';
import { ConditionalRender } from '@/components/ConditionalRender';
import { createVulnerabilityExportConfig } from '@/components/export/configs/vulnerability.export.config';
import {
  buildActiveFilters,
  UniversalExportModal,
} from '@/components/export/universal';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { getBaseRiskStatusOptions } from '@/components/legacy-ui/RiskDropdown';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { GetStartedModal } from '@/components/modals/GetStartedModal';
import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { TableActions } from '@/components/table/types';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFilteredCount } from '@/hooks/useFilteredCount';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { useMy } from '@/hooks/useMy';
import {
  Filter,
  RiskWithVulnerability,
  useRefreshVisited,
  useUpdateRisk,
} from '@/hooks/useRisks';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { VulnerabilityActions } from '@/sections/vulnerabilities/components/actions';
import { CellFiltersProvider } from '@/sections/vulnerabilities/components/cells/CellFiltersContext';
import { VulnerabilitySearchFilters } from '@/sections/vulnerabilities/components/filters';
import { ImportVulnerabilitiesModal } from '@/sections/vulnerabilities/components/modals/ImportVulnerabilitiesModal';
import { VulnerabilityRemoveTag } from '@/sections/vulnerabilities/components/modals/RemoveTagModal';
import { VulnerabilityTag } from '@/sections/vulnerabilities/components/modals/TagModal';
import { createDefaultColumns } from '@/sections/vulnerabilities/config/columnDefinitions';
import {
  VULNERABILITY_GET_STARTED_METHODS,
  type VulnerabilityGetStartedMethodId,
} from '@/sections/vulnerabilities/config/getStartedMethods';
import { VULNERABILITY_DEFAULT_COLUMN_ORDER } from '@/sections/vulnerabilities/config/vulnerabilityColumns.config';
import {
  useGroupedRisks,
  useVulnerabilityColumns,
  useVulnerabilityData,
  useVulnerabilityFilters,
  useVulnerabilityKeyboard,
} from '@/sections/vulnerabilities/hooks';
import {
  getVulnerabilityColumnsStorageKey,
  getVulnerabilityTableRowHeight,
} from '@/sections/vulnerabilities/hooks/useVulnerabilityColumns';
import { GroupableRisk } from '@/sections/vulnerabilities/types/grouping.types';
import { flattenGroupedData } from '@/sections/vulnerabilities/utils/flattenGroupedData.util';
import { getVulnerabilityNoDataConfig } from '@/sections/vulnerabilities/utils/getVulnerabilityNoDataConfig';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import { Risk, RiskSeverity, RiskStatus, SeverityDef } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Component props
 */
export interface VulnerabilitiesProps {
  name?: string;
  parentFilters?: Filter;
  hideFilters?: boolean;
  hideBulkActions?: boolean;
  hideTitle?: boolean;
  inDrawer?: boolean;
  /** When provided in drawer context, limit visible filters to these keys (e.g., ['status', 'severity', 'origin']) */
  drawerFilters?: string[];
  /** Optional icon override for the empty state. Defaults to VulnerabilitiesIcon. */
  emptyStateIcon?: ReactNode;
}

/**
 * Route wrapper component
 */
export function VulnerabilityRoute() {
  return <Vulnerabilities />;
}

/**
 * Default active columns for drawer vulnerability tables
 *
 * Optimized for constrained drawer width (60% viewport):
 * - Removed Tags (100px): Low relevance in asset-focused context
 * - Removed Created (60px): Last Seen more relevant than creation date
 *
 * Total width: 730px (fits 768px drawer comfortably)
 * Previous width: 890px (required horizontal scroll)
 *
 * Note: Users can manually enable via column selector (gear icon)
 * Preferences stored separately: COLUMNS_assetDrawerVulnerabilities
 */
const DRAWER_DEFAULT_COLUMNS = [
  'status',
  'priority',
  'name',
  'origins',
  'visited',
];

/**
 * Main Vulnerabilities component
 */
export function Vulnerabilities({
  name = 'vulnerabilities',
  parentFilters,
  hideFilters,
  hideBulkActions,
  hideTitle,
  inDrawer = false,
  drawerFilters,
  emptyStateIcon,
}: VulnerabilitiesProps) {
  // ==================== Global State ====================
  const { drawerState } = useGlobalState();
  const { isPraetorianUser, isDemoOrg } = useAuth();
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Automatically hide bulk actions in OSINT mode
  const effectiveHideBulkActions = hideBulkActions || isOsint;

  // ==================== Column Setup (before data fetching so activeColumns can gate the query) ====================

  // Column definitions (use compact variant in drawer context)
  const columnDefs = useMemo(
    () => createDefaultColumns(inDrawer ? 'compact' : 'default'),
    [inDrawer]
  );

  /**
   * Select appropriate default columns based on context
   * Main view uses centralized config; drawer uses optimized subset
   */
  const DEFAULT_ACTIVE_COLUMNS = inDrawer
    ? DRAWER_DEFAULT_COLUMNS
    : [...VULNERABILITY_DEFAULT_COLUMN_ORDER];

  // Column management
  const columnManager = useVulnerabilityColumns({
    columns: columnDefs,
    defaultActiveColumns: DEFAULT_ACTIVE_COLUMNS,
    storageKey: getVulnerabilityColumnsStorageKey(),
  });

  // ==================== Data Fetching ====================

  // Optimization #1: Pass sourceOnly parameter to skip unnecessary statistics in drawer
  const { data: vulnerabilityFilters } = useGetVulnerabilityFilters({
    enabled: true, // Always enabled (need source data for display)
    originOnly: inDrawer, // Drawer: 1 query, Main: 8 queries
  });

  // Filter options for export modal pill labels (cached — no extra API call)
  const { filterOptions: vulnFilterOptions, sortedOptions: vulnSortedOptions } =
    useEntityFilters('Vulnerability', 'vuln-table');
  const vulnFieldLabels = useMemo(
    () => Object.fromEntries(vulnSortedOptions.map(o => [o.value, o.label])),
    [vulnSortedOptions]
  );

  // Use mode-aware data hook (legacy mode hides Triaged, new mode shows all)
  const {
    filteredData,
    mode,
    error,
    status,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    filters,
    setFilters,
    isAllEmpty,
    isFilteredEmpty,
    activeQuery,
  } = useVulnerabilityData({
    parentFilters,
    sourceFilterOptions: vulnerabilityFilters?.sourceFilters || [],
    activeColumns: columnManager.activeColumns,
  });

  // Create mode-aware export config (legacy mode excludes Triaged from export)
  const vulnerabilityExportConfig = useMemo(
    () => createVulnerabilityExportConfig(mode),
    [mode]
  );

  // Detect initial loading state to show spinner while data loads
  const isInitialLoading = status === 'pending';

  const { mutateAsync: updateRisk } = useUpdateRisk();

  // Optimization #3: Skip statistics query when hideTitle is true (drawer context)
  // Total count is only displayed in the title, which is hidden in drawers
  // Backend creates: type="my", name="status:O", value=severity (C/H/M/L/I/E)
  const { data: riskStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#my#status:O#now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
      enabled: !hideTitle, // Only fetch when title is shown
    }
  );

  // Compute total count from statistics response
  // Statistics return Open risks by severity (C, H, M, L, I, E) - sum all counts
  const totalDatabaseCount = useMemo(() => {
    if (!riskStatistics) return 0;
    return riskStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [riskStatistics]);

  // Fetch filtered count using the same query as the main data
  const { count: filteredCount, isLoading: isFilteredCountLoading } =
    useFilteredCount(
      {
        resource: 'risk',
        query: activeQuery ?? { node: { labels: ['Risk'] } },
      },
      { enabled: !hideTitle }
    );

  // Build export active filters using shared utility (reverse-maps values to labels)
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filters.advancedFilters as Record<string, unknown> | undefined,
        (vulnFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
          {},
        filters.search,
        vulnFieldLabels
      ),
    [
      filters.search,
      filters.advancedFilters,
      vulnFilterOptions,
      vulnFieldLabels,
    ]
  );

  // ==================== URL Parameter Handling ====================
  const [searchParams] = useSearchParams();

  // Handle URL parameters for deep linking (e.g., drawer keys from share links)
  // Global state already reads these params and opens drawer automatically
  useEffect(() => {
    const drawerKey = searchParams.get('vulnerabilityDrawerKey');
    const drawerOrder = searchParams.get('drawerOrder');

    if (drawerKey && drawerOrder) {
      // Log share link initialization for debugging
      // Note: Global state in useGlobalState automatically handles drawer opening
      // This effect is for component awareness of URL-driven initialization
    }
  }, [searchParams]);

  // ==================== Local State ====================
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [closeModalRisk, setCloseModalRisk] = useState<Risk | null>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<{
    data: Risk[];
    status: string;
  } | null>(null);

  // GetStarted modal workflow state
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);

  // Grouping state (persisted via localStorage in User Profile settings)
  // BUGFIX: Disable grouping in drawer context — useGroupedRisks doesn't accept
  // parentFilters.assetKey, so it fires an unbounded query for ALL tenant risks
  const [isGroupedStorage] = useStorage<boolean>(
    { key: StorageKey.VULNERABILITY_GROUPING },
    false
  );
  const isGrouped = inDrawer ? false : isGroupedStorage;

  // Pending group selection: when checkbox clicked on collapsed group,
  // expand group and auto-select children once they load
  const [pendingGroupSelect, setPendingGroupSelect] = useState<string | null>(
    null
  );

  // ==================== Custom Hooks ====================

  // Filter management
  // Disable URL sync in drawer context to prevent drawer filters from
  // polluting the main page's URL parameters
  const filterHandlers = useVulnerabilityFilters({
    filters,
    setFilters,
    syncWithUrl: !inDrawer,
  });

  // Grouped risks hook (only used when grouping is enabled)
  const {
    groups,
    isLoading: isLoadingGroups,
    expandedGroups,
    toggleGroup,
    childrenByKey,
    fetchNextPage: fetchNextGroupsPage,
    hasNextPage: hasMoreGroups,
    isFetchingNextPage: isFetchingNextGroupsPage,
  } = useGroupedRisks(isGrouped, filters.advancedFilters, filters.search);

  // Determine which data to use: grouped or ungrouped
  // When grouped, GroupableRisk doesn't have 'id' field, so union type needed
  const displayData: RiskWithVulnerability[] | GroupableRisk[] = useMemo(() => {
    if (isGrouped && groups && groups.length > 0) {
      // Flatten grouped data for table rendering (GroupableRisk extends Risk)
      return flattenGroupedData(
        groups,
        expandedGroups,
        childrenByKey,
        filters.sort
      );
    }
    // Use ungrouped data
    return filteredData;
  }, [
    isGrouped,
    groups,
    expandedGroups,
    childrenByKey,
    filteredData,
    filters.sort,
  ]);

  // Selection management — Gmail-style two-step bulk selection
  const bulkSelection = useBulkSelection({
    data: displayData as Array<{ key: string }>,
    activeFilters: (activeQuery ?? {}) as import('@/types').GraphQuery,
    filterVersion: filters,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
  } = bulkSelection;

  const exportSelectedRiskKeys = isSelectAll ? [] : selectedKeys;

  // Derive selectedVulnerabilities from selectedRows for action handlers
  const selectedVulnerabilities = (
    displayData as Array<{ key: string }>
  ).filter(d => selectedKeys.includes(d.key)) as RiskWithVulnerability[];

  // Create a selection-compatible object for existing handler calls
  const selection = {
    selectedKeys,
    setSelectedKeys,
    hasSelection,
    selectedVulnerabilities,
    clearSelection,
    selectedCount,
  };

  // Auto-select children when a pending group selection resolves
  useEffect(() => {
    if (!pendingGroupSelect) return;
    const children = childrenByKey.get(pendingGroupSelect);
    if (children && children.length > 0) {
      const childKeys = children.map(child => child.key);
      // Filter out already-selected keys to avoid duplicates
      const newKeys = childKeys.filter(k => !selectedKeys.includes(k));
      if (newKeys.length > 0) {
        setSelectedKeys([...selectedKeys, ...newKeys]);
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clearing pending state after selection applied, safe pattern for this async use case
      setPendingGroupSelect(null);
    }
  }, [pendingGroupSelect, childrenByKey, setSelectedKeys, selectedKeys]);

  // Keyboard shortcuts
  const keyboard = useVulnerabilityKeyboard({
    enabled: true,
    onKeyboardAction: (code: string, data: Risk[]) => {
      if (!canWriteAssets) return;
      const keyStatus: Record<string, string> = {
        KeyT: RiskStatus.Triaged,
        KeyO: RiskStatus.Opened,
        KeyC: RiskStatus.Remediated,
        KeyA: RiskStatus.Accepted,
        KeyR: 'remediated',
      };
      setConfirmStatus({ data, status: keyStatus[code] });
    },
  });

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    filteredData.length
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filters.advancedFilters,
        sort: filters.sort,
      }),
    [filters.advancedFilters, filters.sort]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Refresh visited timestamp hook for bulk actions
  const { mutateAsync: refreshVisitedAsync, status: refreshVisitedStatus } =
    useRefreshVisited();

  // Risk actions (more actions dropdown) - no longer used after refactor
  // const { moreActions } = useRiskActions({
  //   risks: selection.getSelected(),
  //   onSuccess: refetch,
  //   onNote: () => setIsNoteModalOpen(true),
  // });

  // ==================== Memoized Values ====================

  // Severity filters (used by VulnerabilityActions component)
  const severityFilters = useMemo(
    () => [
      {
        label: SeverityDef[RiskSeverity.Critical],
        value: RiskSeverity.Critical,
        id: RiskSeverity.Critical,
        severity: 'critical',
      },
      {
        label: SeverityDef[RiskSeverity.High],
        value: RiskSeverity.High,
        id: RiskSeverity.High,
        severity: 'high',
      },
      {
        label: SeverityDef[RiskSeverity.Medium],
        value: RiskSeverity.Medium,
        id: RiskSeverity.Medium,
        severity: 'medium',
      },
      {
        label: SeverityDef[RiskSeverity.Low],
        value: RiskSeverity.Low,
        id: RiskSeverity.Low,
        severity: 'low',
      },
      {
        label: SeverityDef[RiskSeverity.Info],
        value: RiskSeverity.Info,
        id: RiskSeverity.Info,
        severity: 'info',
      },
      {
        label: SeverityDef[RiskSeverity.Exposure],
        value: RiskSeverity.Exposure,
        id: RiskSeverity.Exposure,
        severity: 'info',
      },
    ],
    []
  );

  // Note: Filter options are now fetched internally by VulnerabilitySearchFilters
  // via useEntityFilters hook for automatic alphabetical ordering

  // Note: Source display text transformation is now handled by useVulnerabilityData
  // The hook returns both `data` (transformed) and `filteredData` (transformed + mode-filtered)

  // Use centralized hook for detecting active filters
  const hasActiveFilters = useHasActiveFilters(filters);

  // ==================== Event Handlers ====================

  async function handleUpdateRisk(risk: Risk) {
    await updateRisk(risk);
    selection.clearSelection();
  }

  const handleNote = async (note: string): Promise<void> => {
    const selectedRisks = selection.selectedVulnerabilities;
    const promises = selectedRisks.map(risk => {
      return updateRisk({
        key: risk.key,
        name: risk.name,
        status: risk.status,
        comment: note,
        showSnackbar: false,
      });
    });

    await Promise.all(promises);
    const count = selectedRisks.length;
    toast.success(
      count === 1 ? 'Note added to risk' : `Note added to ${count} risks`
    );
    refetch();
  };

  // Handle Get Started method selection
  const handleGetStartedMethodSelect = useCallback(
    (methodId: VulnerabilityGetStartedMethodId) => {
      setIsGetStartedModalOpen(false);

      switch (methodId) {
        case 'integrations': {
          // Build URL with drawer state params for atomic navigation + drawer open
          const drawerParams = new URLSearchParams();
          drawerParams.set('drawerCompositeKey', 'integration');
          drawerParams.set('drawerOrder', JSON.stringify(['integration']));

          navigate(
            `../${AuthenticatedRoute.INTEGRATIONS}?${drawerParams.toString()}`,
            { relative: 'path' }
          );
          break;
        }
        case 'seeds':
          setIsAddSeedModalOpen(true);
          break;
        case 'import':
          setShowImportModal(true);
          break;
      }
    },
    [navigate]
  );

  // Row action menu configuration
  const getRowActions = (risk: Risk | undefined): TableActions => {
    if (!risk || !canWriteAssets) {
      return {
        menu: {
          items: [],
        },
      };
    }

    return {
      menu: {
        items: [
          {
            label: 'Change Severity',
            type: 'label',
          },
          {
            label: 'Critical',
            icon: <ChevronDoubleUpIcon className="size-5" />,
            onClick: () => {
              const currentStatus = getRiskStatusLabel(risk.status).status;
              handleUpdateRisk({
                ...risk,
                status: `${currentStatus}${RiskSeverity.Critical}`,
              });
            },
          },
          {
            label: 'High',
            icon: <ChevronUpIcon className="size-5" />,
            onClick: () => {
              const currentStatus = getRiskStatusLabel(risk.status).status;
              handleUpdateRisk({
                ...risk,
                status: `${currentStatus}${RiskSeverity.High}`,
              });
            },
          },
          {
            label: 'Medium',
            icon: <Bars2Icon className="size-5" />,
            onClick: () => {
              const currentStatus = getRiskStatusLabel(risk.status).status;
              handleUpdateRisk({
                ...risk,
                status: `${currentStatus}${RiskSeverity.Medium}`,
              });
            },
          },
          {
            label: 'Low',
            icon: <ChevronDownIcon className="size-5" />,
            onClick: () => {
              const currentStatus = getRiskStatusLabel(risk.status).status;
              handleUpdateRisk({
                ...risk,
                status: `${currentStatus}${RiskSeverity.Low}`,
              });
            },
          },
          {
            label: 'Info',
            icon: <ChevronDoubleDownIcon className="size-5" />,
            onClick: () => {
              const currentStatus = getRiskStatusLabel(risk.status).status;
              handleUpdateRisk({
                ...risk,
                status: `${currentStatus}${RiskSeverity.Info}`,
              });
            },
          },
          { type: 'divider', label: 'Divider' },
          {
            label: 'Change Status',
            type: 'label',
          },
          ...getBaseRiskStatusOptions(isPraetorianUser, mode).map(option => ({
            label: option.label,
            icon: option.icon,
            onClick: () => {
              const currentSeverity = risk.status[1] || '';
              handleUpdateRisk({
                ...risk,
                status: `${option.value}${currentSeverity}`,
              });
            },
          })),
          {
            label: getStatusLabel(RiskStatus.Deleted, mode),
            icon: <XCircleIcon className="size-5" />,
            onClick: () => {
              setIsCloseModalOpen(true);
              setCloseModalRisk(risk);
            },
          },
        ],
      },
    };
  };

  // ==================== JSX Render ====================
  return (
    <ConditionalRender
      condition={!hideTitle}
      conditionalWrapper={children => (
        <AppRouteBody title="Vulnerabilities">{children}</AppRouteBody>
      )}
    >
      {isInitialLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <>
          {/* Unified Controls Container: Filters + Selection Bar */}
          {!isAllEmpty && (
            <TableControlsContainer
              data-testid="vulnerability-controls"
              filterLabel={!hideFilters}
              filters={
                !hideFilters ? (
                  <VulnerabilitySearchFilters
                    filterHandlers={filterHandlers}
                    isPraetorianUser={isPraetorianUser}
                    isDemoOrg={isDemoOrg}
                    visibleFilters={drawerFilters}
                  />
                ) : null
              }
              filtersRight={
                <SearchBar
                  size="sm"
                  className="h-fit w-64"
                  placeholder="Search"
                  value={filterHandlers.filters.search}
                  onChange={filterHandlers.handleSearchChange}
                />
              }
              selectionBar={
                <>
                  <TableSelectionBar
                    embedded={true}
                    alwaysVisible={true}
                    isVisible={selection.hasSelection}
                    selectedCount={selection.selectedCount}
                    totalCount={
                      filteredCount ?? totalDatabaseCount ?? displayData.length
                    }
                    hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
                    resourceLabel="vulnerabilities"
                    emptySelectionMessage={
                      isFilteredCountLoading
                        ? 'Counting vulnerabilities\u2026'
                        : filteredCount !== undefined
                          ? `${filteredCount.toLocaleString()} vulnerabilities`
                          : `${totalDatabaseCount.toLocaleString()} vulnerabilities`
                    }
                    actions={
                      !effectiveHideBulkActions ? (
                        <VulnerabilityActions
                          vulnerabilities={selection.selectedVulnerabilities}
                          onNoteClick={() => setIsNoteModalOpen(true)}
                          onTagClick={() => setIsTagModalOpen(true)}
                          onRemoveTagClick={() => setIsRemoveTagModalOpen(true)}
                          onImportClick={() => setShowImportModal(true)}
                          onExportClick={() => setShowExportModal(true)}
                          pdfSelectedRows={selection.selectedKeys}
                          onUpdateRisk={handleUpdateRisk}
                          onClearSelection={() => {
                            selection.clearSelection();
                            refetch();
                          }}
                          onScanComplete={() => {
                            refetch();
                          }}
                          onRefreshVisited={(comment: string) => {
                            Promise.allSettled(
                              selection.selectedVulnerabilities.map(risk =>
                                refreshVisitedAsync({ risk, comment })
                              )
                            ).then(() => {
                              selection.clearSelection();
                              refetch();
                            });
                          }}
                          isRefreshingVisited={
                            refreshVisitedStatus === 'pending'
                          }
                          severityOptions={severityFilters}
                          getRiskStatusLabel={getRiskStatusLabel}
                          hideLabel={false}
                          hideActions={false}
                          disabled={selection.selectedCount === 0}
                        />
                      ) : undefined
                    }
                  />
                  {!effectiveHideBulkActions && (
                    <SelectionBanner
                      selectedCount={selection.selectedCount}
                      totalLoadedCount={displayData.length}
                      hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
                      isSelectAll={isSelectAll}
                      isAllLoadedSelected={isAllLoadedSelected}
                      resourceLabel="vulnerabilities"
                      onSelectAllMatching={selectAllMatching}
                      onClearSelection={clearSelection}
                      enableSelectAllMatching={activeQuery != null}
                    />
                  )}
                </>
              }
            />
          )}

          {/* Note Modal */}
          {!effectiveHideBulkActions && (
            <NoteModal
              open={isNoteModalOpen}
              onClose={() => setIsNoteModalOpen(false)}
              onSubmit={handleNote}
              resourceType="Risk"
              mode="simple"
              presentation="modal"
              selectedCount={selection.selectedCount}
            />
          )}

          {/* Tag Modal */}
          {!effectiveHideBulkActions && (
            <VulnerabilityTag
              open={isTagModalOpen}
              onClose={() => setIsTagModalOpen(false)}
              riskKeys={selection.selectedKeys}
              onSuccess={() => {
                selection.clearSelection();
                refetch();
              }}
            />
          )}

          {/* Remove Tag Modal */}
          {!effectiveHideBulkActions && (
            <VulnerabilityRemoveTag
              open={isRemoveTagModalOpen}
              onClose={() => setIsRemoveTagModalOpen(false)}
              riskKeys={selection.selectedKeys}
              onSuccess={() => {
                selection.clearSelection();
                refetch();
              }}
            />
          )}

          {/* Vulnerabilities Table */}
          <CellFiltersProvider
            value={{
              search: filters.search || '',
              advancedFilters: filters.advancedFilters,
            }}
          >
            <Table
              responsive={!inDrawer}
              key={combinedTableKey}
              keyboardNavigation={true}
              sortableColumns={{
                activeColumns: columnManager.activeColumns,
                setActiveColumns: columnManager.setActiveColumns,
                defaultConfig: columnManager.sortableConfig,
              }}
              selection={
                effectiveHideBulkActions
                  ? { hide: true }
                  : {
                      value: selection.selectedKeys,
                      onChange: selection.setSelectedKeys,
                      selectAll: true,
                    }
              }
              onRowClick={(risk, _idx, event) => {
                // Check if this is a group header row
                const riskWithGrouping = risk as unknown as Record<
                  string,
                  unknown
                >;
                if (
                  riskWithGrouping._isGroupHeader &&
                  riskWithGrouping._groupKey
                ) {
                  // Check if this was a checkbox click (has data-checkbox in event path)
                  const target = event?.target as HTMLElement | undefined;
                  const wasCheckboxClick = target?.closest?.('[data-checkbox]');

                  if (
                    wasCheckboxClick &&
                    !expandedGroups.has(riskWithGrouping._groupKey as string)
                  ) {
                    // Checkbox clicked on collapsed group: expand and pending-select
                    setPendingGroupSelect(riskWithGrouping._groupKey as string);
                  }

                  // Toggle group expansion
                  toggleGroup(riskWithGrouping._groupKey as string);
                } else if (!riskWithGrouping._isGroupHeader) {
                  // Open drawer for child rows or ungrouped rows
                  drawerState.handleOpenVulnerabilityDrawer(
                    risk,
                    false,
                    isOsint || undefined
                  );
                }
              }}
              sort={{
                orderBy: filters.sort?.orderBy || 'priority',
                descending: filters.sort?.descending ?? true,
                onSort: sort => {
                  setFilters(prev => ({ ...prev, sort }));
                },
              }}
              rowHeight={getVulnerabilityTableRowHeight()}
              name={name}
              columns={columnManager.columns}
              data={displayData as RiskWithVulnerability[]}
              error={error}
              status={
                isGrouped ? (isLoadingGroups ? 'pending' : 'success') : status
              }
              isFetching={isFetching}
              fetchNextPage={isGrouped ? fetchNextGroupsPage : fetchNextPage}
              isFetchingNextPage={
                isGrouped ? isFetchingNextGroupsPage : isFetchingNextPage
              }
              hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
              noData={getVulnerabilityNoDataConfig({
                isAllEmpty,
                isFilteredEmpty,
                hasActiveFilters,
                onClearFilters: filterHandlers.resetAllFilters,
                onGetStarted: canWriteAssets
                  ? () => setIsGetStartedModalOpen(true)
                  : undefined,
                inDrawer,
                emptyStateIcon,
              })}
              rowActions={getRowActions}
              keyboardActions={{
                ...keyboard.keyboardConfig,
                onKeyboardAction: keyboard.handleKeyboardAction,
              }}
            />
          </CellFiltersProvider>

          {/* Close/Delete Modal */}
          <CloseModal
            open={isCloseModalOpen || keyboard.showDeleteModal}
            onClose={() => {
              keyboard.setShowDeleteModal(false);
              setIsCloseModalOpen(false);
              setCloseModalRisk(null);
            }}
            risks={closeModalRisk ? [closeModalRisk] : []}
            onUpdateRisk={async risk => {
              await handleUpdateRisk(risk);
              refetch();
              selection.clearSelection();
              setCloseModalRisk(null);
            }}
          />

          {/* Confirmation Modal */}
          <Modal
            title="Confirm Change Status"
            open={!!confirmStatus && !keyboard.showDeleteModal}
            onClose={() => {
              setConfirmStatus(null);
            }}
            footer={{
              text: 'Confirm',
              onClick: async () => {
                if (!confirmStatus) return;
                if (confirmStatus.status === 'remediated') {
                  setConfirmStatus(prev => ({
                    data: prev?.data || [],
                    status: 'showCloseModalKeyboard',
                  }));
                  keyboard.setShowDeleteModal(true);
                } else {
                  const count = confirmStatus.data.length;
                  const statusLabel = getStatusLabel(
                    confirmStatus.status as RiskStatus,
                    mode
                  );

                  await Promise.all(
                    confirmStatus.data.map(async risk => {
                      const currentSeverity = risk.status[1];
                      const combinedStatus = `${confirmStatus.status}${currentSeverity}`;
                      const updatedRisk = {
                        ...risk,
                        status: combinedStatus,
                        showSnackbar: false,
                      };
                      await updateRisk(updatedRisk);
                    })
                  );
                  toast.success(
                    count === 1
                      ? `Risk updated to ${statusLabel}`
                      : `${count} risks updated to ${statusLabel}`
                  );
                  selection.clearSelection();
                  setConfirmStatus(null);
                }
              },
            }}
          >
            <div className="pb-4">{`Are you sure you want to update the status of following vulnerabilities to "${confirmStatus?.status === 'remediated' ? getStatusLabel(RiskStatus.Deleted, mode) : getStatusLabel(confirmStatus?.status as RiskStatus, mode)}"?`}</div>
            <div className="scrollbar-dark max-h-[300px] overflow-auto">
              {confirmStatus?.data.map((risk, index) => (
                <div
                  key={risk?.key || index}
                >{`${risk?.name} (${risk?.dns})`}</div>
              ))}
            </div>
          </Modal>

          {/* Import Modal */}
          <ImportVulnerabilitiesModal
            open={showImportModal}
            onClose={() => setShowImportModal(false)}
          />

          {/* Export Modal */}
          <UniversalExportModal
            config={vulnerabilityExportConfig}
            open={showExportModal}
            onClose={() => setShowExportModal(false)}
            selectedRows={exportSelectedRiskKeys}
            onClearSelection={() => {
              selection.clearSelection();
              refetch();
            }}
            activeFilters={exportActiveFilters}
            activeQuery={activeQuery}
          />

          {/* GetStarted Modal */}
          <GetStartedModal
            open={isGetStartedModalOpen}
            onClose={() => setIsGetStartedModalOpen(false)}
            onSelectMethod={handleGetStartedMethodSelect}
            title="Get Started with Vulnerabilities"
            subtitle="Choose how you'd like to discover vulnerabilities:"
            methods={VULNERABILITY_GET_STARTED_METHODS}
          />

          {/* Add Seed Modal (opened from GetStarted seeds method) */}
          <AddSeedByTypeModal
            open={isAddSeedModalOpen}
            onClose={() => setIsAddSeedModalOpen(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({
                predicate: query => {
                  const queryKey = query.queryKey as string[];
                  return queryKey[0] === 'MY' && queryKey[1] === 'risk';
                },
                refetchType: 'active',
              });
              setIsAddSeedModalOpen(false);
            }}
          />
        </>
      )}
    </ConditionalRender>
  );
}
